/**
 * Decrypts $enc$v1$… amount fields returned by analytics finance APIs.
 * Key derivation must match FinanceAmountCryptoService (SHA-256 of amount-v1|secret|sessionToken).
 *
 * Set REACT_APP_BOOKING_ANALYTICS_TOKEN_SECRET to the same value as booking.analytics.token.secret on the server.
 */

const ENC_PREFIX = "$enc$v1$";

const TOKEN_SECRET =
  (typeof process !== "undefined" && process.env?.REACT_APP_BOOKING_ANALYTICS_TOKEN_SECRET) ||
  "REPLACE_WITH_LONG_RANDOM_STRING";

async function sha256Bytes(text) {
  const data = new TextEncoder().encode(text);
  return crypto.subtle.digest("SHA-256", data);
}

async function deriveAesKey(sessionToken) {
  const material = `amount-v1|${TOKEN_SECRET}|${String(sessionToken || "").trim()}`;
  const raw = await sha256Bytes(material);
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["decrypt"]);
}

function isAmountFieldName(name) {
  if (!name || typeof name !== "string") return false;
  const exact = new Set([
    "revenue",
    "revenueDelta",
    "revenueDeltaVsPriorMonth",
    "amountRupee",
    "debitRupee",
    "creditRupee",
    "balanceRupee",
    "totalAmountRupee",
    "bankPaymentRupee",
    "cashPaymentRupee",
    "advanceRupee",
    "openingBalanceRupee",
    "expectedAmountRupee",
    "amountToleranceRupee",
    "netPayableRupee",
    "totalRupee",
    "statedTotalRupee",
    "calculatedTotalRupee",
    "paidSalaryRupee",
    "fixedSalaryRupee",
    "salAdvanceRupee",
    "advanceDeductionRupee",
  ]);
  if (exact.has(name)) return true;
  return name.endsWith("Rupee") || name.endsWith("Paise");
}

function b64UrlToBytes(b64) {
  let s = b64.replace(/-/g, "+").replace(/_/g, "/");
  while (s.length % 4) s += "=";
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

export async function decryptAmountString(enc, sessionToken) {
  if (typeof enc !== "string" || !enc.startsWith(ENC_PREFIX)) {
    const n = Number(enc);
    return Number.isFinite(n) ? n : enc;
  }
  const packed = b64UrlToBytes(enc.slice(ENC_PREFIX.length));
  const iv = packed.slice(0, 12);
  const ct = packed.slice(12);
  const key = await deriveAesKey(sessionToken);
  const plainBuf = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return parseInt(new TextDecoder().decode(plainBuf), 10);
}

async function decryptNode(node, sessionToken, parentKey) {
  if (node == null) return node;
  if (Array.isArray(node)) {
    if (parentKey && isAmountFieldName(parentKey)) {
      return Promise.all(
        node.map((item) => {
          if (typeof item === "string" && item.startsWith(ENC_PREFIX)) {
            return decryptAmountString(item, sessionToken);
          }
          return decryptNode(item, sessionToken, null);
        })
      );
    }
    return Promise.all(node.map((item) => decryptNode(item, sessionToken, null)));
  }
  if (typeof node !== "object") return node;
  const out = { ...node };
  const keys = Object.keys(out);
  await Promise.all(
    keys.map(async (key) => {
      const val = out[key];
      if (typeof val === "string" && val.startsWith(ENC_PREFIX) && isAmountFieldName(key)) {
        out[key] = await decryptAmountString(val, sessionToken);
      } else if (val && typeof val === "object") {
        out[key] = await decryptNode(val, sessionToken, key);
      }
    })
  );
  return out;
}

export async function decryptFinancePayload(data, sessionToken) {
  if (!sessionToken || data == null) return data;
  return decryptNode(data, sessionToken, null);
}

export function isAnalyticsAmountApiUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (!url.includes("/admin/analytics/")) return false;
  if (url.includes("/access/otp") || url.includes("/access/emails")) return false;
  return url.includes("/finance/") || url.includes("/bookings");
}
